import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import styles from './landing.module.css';

import clsxm from '@/lib/clsxm';

import { useTemplateTranslation } from './kit';

/**
 * Long text shows its first screenful with a fade and a "Show more" toggle.
 * The full text is always in the DOM (and in the server render).
 */
export default function Collapsible({
  children,
  maxHeightClass = 'max-h-[22rem]',
  moreLabel,
  footer,
}: {
  children: ReactNode;
  maxHeightClass?: string;
  moreLabel?: string;
  /** Shown on the left of the toggle row. */
  footer?: ReactNode;
}) {
  const { t } = useTemplateTranslation();
  const bodyRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const check = () => setOverflows(node.scrollHeight > node.clientHeight + 4);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(node);
    return () => observer.disconnect();
  }, [expanded]);

  const showToggle = overflows || expanded;

  return (
    <>
      <div
        ref={bodyRef}
        className={clsxm(
          'overflow-hidden',
          !expanded && maxHeightClass,
          !expanded && overflows && styles.fade,
        )}
      >
        {children}
      </div>
      {footer || showToggle ? (
        <div className='mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2'>
          {footer ?? <span />}
          {showToggle ? (
            <button
              type='button'
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              className='text-[13px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
            >
              {expanded ? t('Show less') : moreLabel || t('Show more')}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
